/* iter-49 — Manuscript pane typography + spell-check toolbar.
 *
 * Sits above the prose and drives per-project display settings:
 *   - Font family (curated + manuscript-quality list)
 *   - Font size (12-32 px) — also controlled by Ctrl/Cmd+Wheel and Ctrl/Cmd+/-/+/0
 *   - Line spacing (single 1.0 · 1.5 · double 2.0 · 2.5)
 *   - Paragraph indent + text-alignment
 *   - "Spell-check" button — Claude pass
 *
 * Settings persist per-project via PATCH /api/projects/{id}.manuscript_settings.
 * Browser-native spell-check on any editable textarea is enabled globally
 * via the `spellCheck` attribute (see EditorPanel + CharacterBiblePanel).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';   // Feb-2026 · i18n Wave 2
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Type, Minus, Plus, AlignLeft, AlignJustify,
  Indent, SpellCheck, Loader2, Bold, Italic, Rows3,
  HelpCircle, MessageSquare, RefreshCw, ScanSearch, Lock,
} from 'lucide-react';

export const FONT_PRESETS = [
  // ----- Manuscript-quality serifs -----
  { key: 'cormorant',     label: 'Cormorant Garamond', stack: '"Cormorant Garamond", "Garamond", serif', group: 'Manuscript' },
  { key: 'eb_garamond',   label: 'EB Garamond',         stack: '"EB Garamond", "Garamond", serif',        group: 'Manuscript' },
  { key: 'crimson',       label: 'Crimson Pro',         stack: '"Crimson Pro", "Crimson Text", "Georgia", serif', group: 'Manuscript' },
  { key: 'libre_caslon',  label: 'Libre Caslon Text',   stack: '"Libre Caslon Text", "Georgia", serif',   group: 'Manuscript' },
  { key: 'lora',          label: 'Lora',                stack: '"Lora", "Georgia", serif',                 group: 'Manuscript' },
  // ----- System serifs -----
  { key: 'garamond',      label: 'Garamond',            stack: 'Garamond, "Adobe Garamond Pro", "Georgia", serif', group: 'System serif' },
  { key: 'georgia',       label: 'Georgia',             stack: 'Georgia, "Times New Roman", serif',       group: 'System serif' },
  // ----- Sans + mono (for proofing) -----
  { key: 'inter',         label: 'Inter',               stack: '"Inter", system-ui, sans-serif',          group: 'Proofing' },
  { key: 'jetbrains',     label: 'JetBrains Mono',      stack: '"JetBrains Mono", "Menlo", monospace',    group: 'Proofing' },
];

export const FONT_BY_KEY = FONT_PRESETS.reduce((acc, f) => { acc[f.key] = f; return acc; }, {});

export const LINE_SPACING_OPTIONS = [
  { key: '1.0', label: 'Single',     value: 1.0 },
  { key: '1.5', label: '1.5',        value: 1.5 },
  { key: '2.0', label: 'Double',     value: 2.0 },   // ← default per user spec
  { key: '2.5', label: '2.5',        value: 2.5 },
];

export const PARA_SPACING_OPTIONS = [
  { key: 'compact',    label: 'Compact',     spaceClass: 'space-y-2',  em: 0.5  },
  { key: 'normal',     label: 'Normal',      spaceClass: 'space-y-5',  em: 1.25 },
  { key: 'spacious',   label: 'Spacious',    spaceClass: 'space-y-8',  em: 2.0  },
  { key: 'double',     label: 'Double',      spaceClass: 'space-y-12', em: 3.0  },
];

export const DEFAULT_MANUSCRIPT_SETTINGS = {
  font_family:              'cormorant',
  font_size_px:             17,
  line_height:              2.0,    // double-spaced default
  paragraph_indent_em:      0,
  text_align:               'left',
  font_weight:              'normal',
  font_style:               'normal', // 'normal' | 'italic'
  paragraph_spacing_key:    'normal', // compact | normal | spacious | double
};

const MIN_SIZE = 12;
const MAX_SIZE = 32;

export function clampSize(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return DEFAULT_MANUSCRIPT_SETTINGS.font_size_px;
  return Math.max(MIN_SIZE, Math.min(MAX_SIZE, Math.round(v)));
}

export function ManuscriptToolbar({
  settings = DEFAULT_MANUSCRIPT_SETTINGS,
  onChange,
  onRunSpellCheck,
  spellChecking = false,
  onOpenRewrite,
  onRunContinuityAudit,
  continuityBusy = false,
  canContinuityAudit = false,
  canScriptConvert = false,
}) {
  const { t } = useTranslation();
  const s = { ...DEFAULT_MANUSCRIPT_SETTINGS, ...(settings || {}) };
  const patch = (delta) => onChange?.({ ...s, ...delta });

  return (
    <div
      data-testid="manuscript-toolbar"
      className="flex flex-wrap items-center gap-1.5 border-b border-panel-border bg-panel-header/40 px-3 py-1.5 text-[13.5px]"
    >
      {/* Font family */}
      <div className="flex items-center gap-1">
        <Type className="h-3.5 w-3.5 text-muted-foreground" />
        <Select value={s.font_family} onValueChange={(v) => patch({ font_family: v })}>
          <SelectTrigger
            data-testid="manuscript-font-trigger"
            className="h-7 w-[150px] border-border bg-card text-[13.5px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="text-[13.5px]">
            {FONT_PRESETS.map((f) => (
              <SelectItem
                key={f.key}
                value={f.key}
                data-testid={`manuscript-font-opt-${f.key}`}
                style={{ fontFamily: f.stack }}
              >
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <span className="mx-1 h-4 w-px bg-panel-border" />

      {/* Font size */}
      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => patch({ font_size_px: clampSize(s.font_size_px - 1) })}
          data-testid="manuscript-size-down"
          title={t('editor.sizeDown')}
        >
          <Minus className="h-3 w-3" />
        </Button>
        <span
          className="min-w-[28px] text-center font-mono text-[13px] tabular-nums text-muted-foreground"
          data-testid="manuscript-size-display"
        >
          {clampSize(s.font_size_px)}px
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => patch({ font_size_px: clampSize(s.font_size_px + 1) })}
          data-testid="manuscript-size-up"
          title={t('editor.sizeUp')}
        >
          <Plus className="h-3 w-3" />
        </Button>
      </div>

      <span className="mx-1 h-4 w-px bg-panel-border" />

      {/* Bold — toggles the whole manuscript between normal/bold weight. */}
      <Button
        variant="ghost"
        size="sm"
        className={`h-7 w-7 p-0 ${s.font_weight === 'bold' ? 'text-primary bg-primary/10' : ''}`}
        onClick={() => patch({ font_weight: s.font_weight === 'bold' ? 'normal' : 'bold' })}
        data-testid="manuscript-bold-toggle"
        title={t('editor.boldToggle')}
        aria-pressed={s.font_weight === 'bold'}
      >
        <Bold className="h-3.5 w-3.5" />
      </Button>
      {/* Italic — toggles the whole manuscript between normal/italic style. */}
      <Button
        variant="ghost"
        size="sm"
        className={`h-7 w-7 p-0 ${s.font_style === 'italic' ? 'text-primary bg-primary/10' : ''}`}
        onClick={() => patch({ font_style: s.font_style === 'italic' ? 'normal' : 'italic' })}
        data-testid="manuscript-italic-toggle"
        title="Toggle italic manuscript view"
        aria-pressed={s.font_style === 'italic'}
      >
        <Italic className="h-3.5 w-3.5" />
      </Button>

      <span className="mx-1 h-4 w-px bg-panel-border" />

      {/* Line spacing */}
      <Select value={String(s.line_height)} onValueChange={(v) => patch({ line_height: Number(v) })}>
        <SelectTrigger
          data-testid="manuscript-spacing-trigger"
          className="h-7 w-[100px] border-border bg-card text-[13.5px]"
          title="Line spacing"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LINE_SPACING_OPTIONS.map((o) => (
            <SelectItem
              key={o.key}
              value={String(o.value)}
              data-testid={`manuscript-spacing-opt-${o.key}`}
            >
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Paragraph spacing — space between paragraphs */}
      <Select
        value={s.paragraph_spacing_key || 'normal'}
        onValueChange={(v) => patch({ paragraph_spacing_key: v })}
      >
        <SelectTrigger
          data-testid="manuscript-para-spacing-trigger"
          className="h-7 w-[100px] border-border bg-card text-[13.5px]"
          title="Paragraph spacing"
        >
          <Rows3 className="mr-1 h-3 w-3 text-muted-foreground" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PARA_SPACING_OPTIONS.map((o) => (
            <SelectItem
              key={o.key}
              value={o.key}
              data-testid={`manuscript-para-spacing-opt-${o.key}`}
            >
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <span className="mx-1 h-4 w-px bg-panel-border" />

      {/* Paragraph indent + alignment */}
      <Button
        variant="ghost"
        size="sm"
        className={`h-7 w-7 p-0 ${s.paragraph_indent_em ? 'text-primary' : ''}`}
        onClick={() => patch({ paragraph_indent_em: s.paragraph_indent_em ? 0 : 2 })}
        data-testid="manuscript-indent-toggle"
        title={t('editor.indent')}
      >
        <Indent className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={`h-7 w-7 p-0 ${s.text_align === 'left' ? 'text-primary' : ''}`}
        onClick={() => patch({ text_align: 'left' })}
        data-testid="manuscript-align-left"
        title={t('editor.alignLeft')}
      >
        <AlignLeft className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={`h-7 w-7 p-0 ${s.text_align === 'justify' ? 'text-primary' : ''}`}
        onClick={() => patch({ text_align: 'justify' })}
        data-testid="manuscript-align-justify"
        title={t('editor.alignJustify')}
      >
        <AlignJustify className="h-3.5 w-3.5" />
      </Button>

      <span className="ml-auto" />

      {/* Rewrite Dialogue */}
      {onOpenRewrite && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenRewrite}
          data-testid="manuscript-rewrite-btn"
          className="h-7 gap-1.5 px-2 text-[13.5px] text-primary/80 hover:text-primary"
          title="AI Rewrite Dialogue — choose model, set structure and run a chapter-by-chapter rewrite plan"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Rewrite
        </Button>
      )}

      {/* Continuity Audit add-on ($9.99 pay-per-use) */}
      {onRunContinuityAudit && (
        <Button
          variant="ghost"
          size="sm"
          onClick={canContinuityAudit ? onRunContinuityAudit : undefined}
          disabled={continuityBusy || !canContinuityAudit}
          data-testid="manuscript-continuity-audit-btn"
          data-locked={!canContinuityAudit ? 'true' : undefined}
          className="h-7 gap-1.5 px-2 text-[13.5px] text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 disabled:opacity-50"
          title={canContinuityAudit
            ? 'Run a full Continuity Audit — catches character, timeline and world-building contradictions across all chapters'
            : 'Continuity Audit — purchase from Add-ons or upgrade to Publisher'}
        >
          {continuityBusy
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : canContinuityAudit
              ? <ScanSearch className="h-3.5 w-3.5" />
              : <Lock className="h-3.5 w-3.5" />}
          Continuity
        </Button>
      )}

      {/* Spell-check (Claude) */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onRunSpellCheck}
        disabled={spellChecking}
        data-testid="manuscript-spellcheck-btn"
        className="h-7 gap-1.5 px-2 text-[13.5px]"
        title={t('editor.spellCheckTitle')}
      >
        {spellChecking
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <SpellCheck className="h-3.5 w-3.5 text-primary" />}
        {t('editor.spellCheck')}
      </Button>

      {/* AI Help — moved here from floating corner button (Feb-2026).
       * Dispatches a custom event so the dialog (AiHelpLauncher in App.js)
       * can open without prop-drilling through the toolbar chain. */}
      <div className="ml-auto shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => window.dispatchEvent(new CustomEvent('loom:open-ai-help'))}
          data-testid="manuscript-ai-help-btn"
          className="h-7 gap-1.5 px-2 text-[13.5px] text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
          title="Ask the Loom AI for writing help (Cmd/Ctrl+/)"
        >
          <HelpCircle className="h-3.5 w-3.5" />
          AI Help
        </Button>
      </div>
    </div>
  );
}
